import { useTranslation } from 'next-i18next';
import { FC, useCallback } from 'react';
import { IWedding_Website_Section } from '@bridebook/models/source/models/Weddings.types';
import { WebsiteTileVariant } from '@bridebook/web/components/wedding-website/view-mode/partials/tile-component/wedding-website-tile-component';
import { WeddingWebsiteOurStory } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/wedding-website-our-story';
import { WeddingWebsitePhoto } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/wedding-website-photo';
import { WeddingWebsiteQuestionAnswerField } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/wedding-website-question-answer';
import { WeddingWebsiteTextField } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/wedding-website-text-field';
import { WeddingWebsiteVenue } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/wedding-website-venue';
import {
  faqSection,
  ourStorySection,
} from '@bridebook/web/lib/wedding-website/constants/emptyWebsiteTemplate';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';

interface IProps {
  section: IWedding_Website_Section;
  palette: Palettes;
}

export const WeddingWebsiteMappedTile: FC<IProps> = ({ section, palette = Palettes.Bridebook }) => {
  const { t } = useTranslation('weddingWebsite');
  const getSectionTitle = useCallback(() => {
    switch (section.name) {
      case ourStorySection.name: {
        return t('previewMode.tile.ourStory.title');
      }
      case faqSection.name: {
        return t('previewMode.tile.faq.title2');
      }
      default: {
        return section.name;
      }
    }
  }, [section.name, t]);

  if (!section.visible) return null;

  switch (section.type) {
    case 'VenueField': {
      return (
        <WeddingWebsiteVenue
          variant={WebsiteTileVariant.Primary}
          palette={palette}
          section={section}
        />
      );
    }
    case 'PhotosField': {
      const photoPath = section.data[0]?.path;

      return photoPath ? <WeddingWebsitePhoto url={photoPath} /> : null;
    }
    case 'TextField': {
      const variant =
        palette === Palettes.Foliage ? WebsiteTileVariant.Tertiary : WebsiteTileVariant.Secondary;

      if (section.name === ourStorySection.name) {
        return (
          <WeddingWebsiteOurStory
            variant={variant}
            palette={palette}
            title={getSectionTitle()}
            section={section}
          />
        );
      }

      return (
        <WeddingWebsiteTextField
          variant={variant}
          palette={palette}
          title={getSectionTitle()}
          sectionName={section.name}>
          {section.data.value}
        </WeddingWebsiteTextField>
      );
    }
    case 'QuestionAnswerField': {
      return (
        <WeddingWebsiteQuestionAnswerField
          variant={WebsiteTileVariant.Secondary}
          palette={palette}
          section={section}
          title={getSectionTitle()}
        />
      );
    }
    default:
      return null;
  }
};
